import React, { useState, useEffect, useRef } from 'react';
import axios from 'cccisd-axios';
import { client } from 'cccisd-apollo';
import Modal from 'cccisd-modal';
import { ResourceCenterWithCollection } from 'cccisd-laravel-resources';
import notification from 'cccisd-notification';
import Confirm from 'cccisd-confirm';
import Loader from 'cccisd-loader';
import { formatResourcesForCollection, filterSessionSettings } from 'js/helpers';
import _uniq from 'lodash/uniq';
import getPlanQuery from 'js/queries/getPlan.graphql';
import getResourcesQuery from 'js/queries/getResources.graphql';
import style from './style.css';

const AppDefs = window.cccisd.appDefs;
const Fortress = window.cccisd.fortress;
const Boilerplate = window.cccisd.boilerplate;

const EditAssessmentPlan = props => {
    const [loading, setLoading] = useState(true);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState({});
    const [planResources, setPlanResources] = useState([]);
    const [editType, setEditType] = useState('add');

    const editModalRef = useRef(null);
    const handle = props.assessmentLibraryHandle || 'default';
    let config = props.config;
    if (!config && AppDefs.resourceCenters && AppDefs.resourceCenters.list) {
        config = AppDefs.resourceCenters.list.find(rc => rc.id === handle);
    }

    useEffect(() => {
        editAssessmentPlan(props.pawnId, props.planId, props.type);
    }, []);

    const onSubmitEdit = async vals => {
        const requests = vals.respondents
            .map(r => r.id)
            .map(respondentId => {
                return axios.put(
                    window.cccisd.boilerplate.route('api.assignment.plan.update', {
                        plan: selectedPlan.assignmentPlanId,
                    }),
                    {
                        creatingPawnId: Fortress.user.acting.id,
                        label: vals.planName,
                        description: vals.planDescription,
                        pawnIds: [respondentId],
                        sessions: vals.sessions.map(session => {
                            return {
                                sessionId: session.sessionId,
                                label: session.label,
                                deploymentId: session.deploymentId,
                                sessionType: session.sessionType,
                                offset: session.offset,
                                opensAt: session.opensAt,
                                closesAt: session.closesAt,
                                schedule: session.schedule,
                                sessionDeploymentInfo: session.sessionDeploymentInfo,
                                settings: filterSessionSettings(session.settings),
                            };
                        }),
                    }
                );
            });
        return axios.all(requests).then(responses => {
            props.setForceReloadQueries();
            props.setIsEditing(false);
        });
    };

    const onDelete = async planId => {
        await axios.delete(Boilerplate.route('api.assignment.plan.delete', { plan: planId }));

        props.setForceReloadQueries();
        props.setIsEditing(false);
    };

    const getPlan = async (pawnId, planId) => {
        let resp;
        try {
            resp = await client.query({
                query: getPlanQuery,
                variables: {
                    pawnId,
                    planId,
                },
                fetchPolicy: 'network-only',
            });

            setSelectedPlan(resp.data.flows.assignmentPlan);
        } catch (e) {
            throw e;
        }

        return resp;
    };

    const editAssessmentPlan = async (pawnId, planId, type) => {
        setLoading(true);
        await getPlan(pawnId, planId).then(async resp => {
            const resourceIds = _uniq(
                resp.data.flows.assignmentPlan.sessionList.map(session => session.settings.resourceIds).flat()
            );
            let resourcesResp;
            try {
                if (resourceIds && resourceIds.length > 0) {
                    resourcesResp = await client.query({
                        query: getResourcesQuery,
                        variables: {
                            resourceIds,
                        },
                        fetchPolicy: 'network-only',
                    });
                    setPlanResources(resourcesResp.data.resources.resourceList);
                }
            } catch (e) {
                throw e;
            }
        });
        setLoading(false);
        setSelectedStudents([{ id: pawnId }]);
        setEditType(type);
        editModalRef.current.open();
    };

    const renderResourceTiles = (data, renderDefault) => {
        return <div className={style.resourceTiles}>{renderDefault(data)}</div>;
    };

    if (loading) {
        return <Loader loading />;
    }

    return (
        <div>
            <Modal
                trigger={null}
                size="99vw"
                ref={editModalRef}
                beforeClose={() => {
                    props.setForceReloadQueries();
                    props.setIsEditing(false);
                }}
                title={props.title || 'Edit Assessments'}
            >
                <ResourceCenterWithCollection
                    handle={props.assessmentLibraryHandle || 'default'}
                    collectionUserList={selectedStudents}
                    collectionBuckets={
                        props.collectionBuckets || ['Student', 'Caregiver', 'Educator', 'Mental Health Provider']
                    }
                    onSubmit={onSubmitEdit}
                    editCollection={editType}
                    selectedPlan={selectedPlan}
                    onDelete={() => {
                        const studentCount = selectedPlan.selectedByPawnCount;
                        const isPlural = studentCount > 1;
                        const studentLabel = isPlural ? 'students' : 'student';

                        Confirm({
                            message: 'Are you sure?',
                            description: `Are you sure you want to delete? This is currently assigned to ${studentCount} ${studentLabel}.`,
                        }).then(() => {
                            onDelete(selectedPlan.assignmentPlanId);
                            notification({
                                message: 'Assigned Assessments deleted.',
                                type: 'success',
                            });
                        });
                    }}
                    planResources={formatResourcesForCollection(planResources)}
                    addToCollectionButtonLabel="Add"
                    renderResourceTiles={props.renderResourceTiles || renderResourceTiles}
                    progressQuery={props.progressQuery}
                />
            </Modal>
        </div>
    );
};

export default EditAssessmentPlan;
